import { Operation, OperationListResponse, MissionFilter } from '../types/operation';
import { API_URL } from '../constants/api';

// API endpoint для операций
const OPERATIONS_API_URL = `${API_URL}/api/operations`;

// Обычный запрос к API без авторизации
async function fetchData(url: string, options: RequestInit = {}) {
  try {
    console.log(`Making public request to: ${url}`);
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    console.log('Status of response:', response.status);

    if (!response.ok) {
      throw new Error(`Error during request: ${response.status} ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error during request execution:', error);
    throw error;
  }
}

// Интерфейс для фильтров операций (соответствует OperationFilterRequest на backend)
export interface OperationFilter {
  type?: string;
  difficulties?: string[];
  minDuration?: number;
  maxDuration?: number;
  minPrice?: number;
  maxPrice?: number;
  minPlayers?: number;
  maxPlayers?: number;
  tags?: string[];
  searchTerm?: string;
}

// Получить все операции с фильтрацией (публичный запрос)
export const getOperations = async (
  page = 1,
  pageSize = 20,
  filter?: OperationFilter
): Promise<{operations: Operation[], totalCount: number, facets?: any}> => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });

  // Добавляем параметры фильтрации если они переданы
  if (filter) {
    if (filter.type) {
      params.append('filter.Type', filter.type);
    }
    if (filter.difficulties && filter.difficulties.length > 0) {
      filter.difficulties.forEach(difficulty => params.append('filter.Difficulties', difficulty));
    }
    if (filter.minDuration !== undefined) {
      params.append('filter.MinDuration', filter.minDuration.toString());
    }
    if (filter.maxDuration !== undefined) {
      params.append('filter.MaxDuration', filter.maxDuration.toString());
    }
    if (filter.minPrice !== undefined) {
      params.append('filter.MinPrice', filter.minPrice.toString());
    }
    if (filter.maxPrice !== undefined) {
      params.append('filter.MaxPrice', filter.maxPrice.toString());
    }
    if (filter.minPlayers !== undefined) {
      params.append('filter.MinPlayers', filter.minPlayers.toString());
    }
    if (filter.maxPlayers !== undefined) {
      params.append('filter.MaxPlayers', filter.maxPlayers.toString());
    }
    if (filter.tags && filter.tags.length > 0) {
      filter.tags.forEach(tag => params.append('filter.Tags', tag));
    }
    if (filter.searchTerm) {
      params.append('filter.SearchTerm', filter.searchTerm);
    }
  }

  return fetchData(`${OPERATIONS_API_URL}?${params.toString()}`);
};

// Получить операцию по ID через обычный API
export const getOperationById = async (id: string): Promise<Operation> => {
  try {
    console.log(`Getting operation with ID: ${id} via regular API`);
    const data = await fetchData(`${OPERATIONS_API_URL}/${id}`);

    console.log("Operation data received:", data);

    // Если данные получены в другом формате, преобразуем их
    if (data) {
      // Убедимся, что все необходимые поля присутствуют
      return {
        id: data.id || '',
        title: data.title || '',
        name: data.title || '', // Используем title как name если отсутствует
        description: data.description || '',
        price: typeof data.price === 'number' ? data.price : 0,
        difficulty: data.difficulty || 'Medium',
        duration: data.duration || 120,
        type: data.type || 'Airsoft',
        gameType: data.type || 'Airsoft', // Используем type как gameType если отсутствует
        images: Array.isArray(data.images) ? data.images : [],
        videos: Array.isArray(data.videos) ? data.videos : [],
        files: Array.isArray(data.files) ? data.files : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        isActive: data.isActive !== undefined ? data.isActive : true,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
        playerCountMin: data.playerCountMin || 4,
        playerCountMax: data.playerCountMax || 12
      };
    }

    throw new Error("Failed to get operation data");
  } catch (error) {
    console.error(`Error fetching operation with ID ${id}:`, error);
    throw new Error("Error loading mission data");
  }
};

// Re-export for backward compatibility
export type ProductFilter = OperationFilter;
export const getProducts = getOperations;
export const getProductById = getOperationById;
